'use client';

import { Suspense, useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { domain_url } from '@/constants/apiConstant';
import { blogsQueryParams } from '@/constants/queryParams/blogsQueryParams';
import BlogsLayouts from '@/layouts/BlogsLayouts';
import { buildQueryString } from '@/utils/buildQueryString';
import { formatFee } from '@/utils/formatFees';
import { removeSpace } from '@/utils/removeSpace';

import apiRequest from '@/lib/api/apiHelper';
import BlogBanner from '@/components/blogs/BlogBanner';
import FeaturedBlog from '@/components/blogs/FeaturedBlog';
import TrendingBlog from '@/components/blogs/TrendingBlog';
import BlogsCard from '@/components/common/BlogsCard';
import CourseCard from '@/components/common/CourseCard';
import Breadcrumb from '@/components/common/seo/BreadCrumbs';
import Separator from '@/components/common/Separator';

const BlogsClientComponent = ({ featuredBlogs, topBlogs, categoriesData }: any) => {
  const searchParams = useSearchParams();
  const [sortedBlogs, setSortedBlogs] = useState<any>();
  const [page, setPage] = useState<number>(9);
  const [total, setTotal] = useState<number>(0);
  const [allCount, setAllCount] = useState<number>(0);
  const [topCourses, setTopCourses] = useState<any>([]);

  const handleOpen = () => {
    console.log('open clicked');
  };

  const formatCategory = (id: string) => {
    return id.replace(/-/g, ' ').replace(/&/g, ' & ');
  };

  const categoryParam = searchParams.get('category') ?? 'all';
  const formattedCategory = formatCategory(categoryParam);

  const fetchBlogsByCategory = async (category: string, isLoadMore = false) => {
    const queryParams = {
      ...blogsQueryParams,
      filters: category !== 'all' ? { categories: { category_name: { $eqi: category } } } : {},
      pagination: {
        page: isLoadMore ? page + 1 : 1,
        pageSize: 9,
      },
    };

    const response: any = await apiRequest(`/blogs?${buildQueryString(queryParams)}`, 'GET');

    setTotal(response?.data?.data?.pagination?.total ?? 0);
    if (category === 'all') {
      setAllCount(response?.data?.data?.pagination?.total ?? 0);
    }

    if (isLoadMore) {
      setPage((prev) => prev + 1);
      setSortedBlogs((prev: any) => ({
        ...prev,
        results: [...(prev?.results ?? []), ...response.data.data.results],
      }));
    } else {
      setPage(1);
      setSortedBlogs(response?.data?.data);
    }
  };

  const fetchTopCourses = async (category: any) => {
    let streamId = null;

    if (category !== 'all') {
      const selectedCategory = categoriesData?.results?.find(
        (cat: any) => removeSpace(cat?.category_name) === category,
      );
      streamId = selectedCategory?.streams?.[0]?.id;
    }

    const queryParams = {
      fields: ['course_name', 'abbreviation', 'is_top', 'course_url'],
      populate: {
        average_fees: {
          fields: ['average_fee'],
        },
        course_navbars: {
          fields: ['title'],
        },
        course_duration: {
          fields: ['duration'],
        },
        page_data: {
          fields: ['id'],
        },
        courses_type: {
          fields: ['course_type'],
        },
        colleges: {
          count: true,
        },
      },
      filters: streamId ? { streams: { id: { $eq: streamId } } } : { is_top: true },
      pagination: { pageSize: 3 },
    };

    const response: any = await apiRequest(`/courses?${buildQueryString(queryParams)}`, 'GET');

    setTopCourses(response?.data?.data?.results ?? []);
  };

  useEffect(() => {
    fetchBlogsByCategory(formattedCategory);
    fetchTopCourses(categoryParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryParam, formattedCategory]);

  const loadMore = () => {
    if (sortedBlogs?.results?.length < total) {
      fetchBlogsByCategory(formattedCategory, true);
    }
  };

  const breadcrumbItems = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: `${domain_url}/`,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Blogs',
      item: `${domain_url}/blogs`,
    },
  ];

  return (
    <>
      
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: breadcrumbItems,
            }),
          }}
        />
      
      <BlogsLayouts>
        <h1 className="text-center text-lg font-semibold text-primary lg:text-2xl">CollegeHai Blogs</h1>
        <div className="mb-2">
          <Breadcrumb items={breadcrumbItems} color="text-black" />
        </div>

        <FeaturedBlog featuredBlogs={featuredBlogs} />

        <div className="mx-auto my-5 w-full px-4 md:my-10 lg:max-w-[73.75rem] 2xl:max-w-screen-xl">
          <Separator />
        </div>

        <TrendingBlog topBlogs={topBlogs} />

        <div className="mx-auto my-5 w-full px-4 md:my-5 lg:max-w-[73.75rem] 2xl:max-w-screen-xl">
          <Separator />
        </div>

        {/* category wise blogs */}
        <section className="w-full">
          <h2 className="mb-5 text-2xl font-semibold text-primary underline underline-offset-2">Blogs by Category</h2>
          <div className="hide-scrollbar flex justify-items-center gap-5 overflow-auto px-4 text-center">
            <Link
              href="/blogs"
              scroll={false}
              className={`flex w-full min-w-40 max-w-40 cursor-pointer flex-col items-center justify-center rounded-lg border bg-slate-50 py-2 text-sm ${
                !searchParams.get('category') ? 'border-primary text-primary' : ''
              }`}
            >
              <p className="pt-2 leading-tight">All</p>
              <p className="text-xs text-gray-500">{allCount} Blogs</p>
            </Link>

            {categoriesData?.results?.map((category: any) => {
              const cleanedName = removeSpace(category?.category_name);
              const isActive = categoryParam === cleanedName;

              return (
                <Link
                  key={category?.id}
                  href={`/blogs?category=${cleanedName}`}
                  scroll={false}
                  className={`flex w-full min-w-40 max-w-40 cursor-pointer flex-col items-center justify-center rounded-lg border bg-slate-50 py-2 text-sm ${
                    isActive ? 'border-primary text-primary' : ''
                  }`}
                >
                  {category?.logo?.url && (
                    <Image src={category?.logo?.url} alt={category?.logo?.name ?? 'category'} width={30} height={30} />
                  )}

                  <p className="pt-2 capitalize leading-tight text-tertiary">{category?.category_name}</p>
                  <p className="text-xs text-gray-500">{category?.blogs?.count} Blogs</p>
                </Link>
              );
            })}
          </div>
        </section>

        <div className="grid grid-cols-1 gap-6  md:grid-cols-2 lg:grid-cols-3">
          {sortedBlogs?.results?.map((item: any) => (
            <div key={item?.id} className="h-full">
              <BlogsCard blogData={item} />
            </div>
          ))}
        </div>

        {sortedBlogs?.results?.length < total && (
          <div className="my-5 flex items-center justify-center">
            <button onClick={loadMore} className="mt-6 rounded-md bg-primary px-4 py-2 text-white">
              Load More
            </button>
          </div>
        )}

        <BlogBanner handleOpen={handleOpen} />

        <section className="flex flex-col gap-y-2">
          <h2 className="mb-5 text-lg font-semibold text-primary underline underline-offset-2 lg:text-2xl">
            Recommended Courses
          </h2>
          <div className="hide-scrollbar flex items-center gap-x-4 overflow-auto">
            {topCourses?.map((course: any) => {
              const numberOfColleges = course?.colleges?.count;
              const avgFee = formatFee(course?.average_fees?.average_fee);
              const courseNavbar = course?.course_navbars?.[0]?.title ?? 'Course Info';

              return (
                <CourseCard
                  key={course?.id}
                  course={course}
                  courseNavbar={courseNavbar}
                  avgFee={avgFee}
                  numberOfColleges={numberOfColleges}
                />
              );
            })}
          </div>
        </section>
      </BlogsLayouts>
    </>
  );
};

export default function BlogsPage({ featuredBlogs, topBlogs, categoriesData }: any) {
  return (
    <div className="h-fit min-h-[1890px] md:min-h-[2400px] lg:min-h-[2060px]">
      <Suspense fallback={<div />}>
        <BlogsClientComponent featuredBlogs={featuredBlogs} topBlogs={topBlogs} categoriesData={categoriesData} />
      </Suspense>
    </div>
  );
}
